import { useMemo, useState } from "react";
import { Archive, ArrowLeft, Plus, SquareKanban, Table2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArchiveDrawer } from "../batches/archive-drawer";
import { BatchesTable } from "../batches/batches-table";
import { KanbanBoard } from "../batches/kanban-board";
import { useActiveBatches, useArchive, useBatchAction } from "../batches/queries";
import type { Batch } from "../batches/types";
import { WORKFLOW_BOARD_COLUMNS, WORKFLOW_COLUMN_STATUSES } from "../batches/types";
import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import { FilterPopover, type FilterState } from "../../components/ui/filter-popover";
import { cn } from "../../lib/utils";
import { getWorkflow } from "./api";

export function AutomationRunsBatchesPage({ workflowId }: { workflowId: string }) {
  const nav = useNavigate();
  const [view, setView] = useState<"board" | "table">("board");
  const [archive, setArchive] = useState<{ status: string } | null>(null);
  const [filters, setFilters] = useState<FilterState>({ status: "all" });
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({
    ...presetRange("last_30"),
    preset: "last_30",
  }));

  const { data: workflow } = useQuery({
    queryKey: ["call-workflow", workflowId],
    queryFn: () => getWorkflow(workflowId),
    enabled: !!workflowId,
  });

  const { data: rawBatches = [], isLoading } = useActiveBatches(true, "current", workflowId);
  const batches = useMemo(
    () =>
      rawBatches.map((b) => {
        const activeCalls = Array.isArray(b.active_calls) ? b.active_calls : [];
        const activeCallsCount = activeCalls.length || Number(b.active_calls_count || 0);
        return { ...b, status: activeCallsCount > 0 ? "running" : b.status };
      }),
    [rawBatches],
  );
  const mutation = useBatchAction();

  const { data: archiveData } = useArchive(
    { from: "", to: "", status: "all", q: filters.search || "", workflow_id: workflowId },
    Boolean(filters.search),
  );
  const archiveResults = useMemo(() => {
    if (!filters.search || !archiveData?.items) return [] as Batch[];
    const activeIds = new Set(batches.map((b) => b.schedule_id));
    return archiveData.items.filter((b) => !activeIds.has(b.schedule_id));
  }, [filters.search, archiveData, batches]);

  const handleMenu = (b: Batch, action: string) => {
    if (action === "pause") {
      mutation.mutate({ id: b.schedule_id, patch: { status: "paused" } });
    } else if (action === "resume") {
      mutation.mutate({ id: b.schedule_id, patch: { status: "resume" } });
    } else if (action === "cancel-remainder") {
      mutation.mutate({ id: b.schedule_id, patch: { status: "completed" } });
    }
  };

  const openRuns = (batch: Batch) => {
    nav(
      `/home/automation/runs?workflowId=${encodeURIComponent(workflowId)}&scheduleId=${encodeURIComponent(batch.schedule_id)}`,
    );
  };

  const scheduleHref = `/home/automation/schedule?workflowId=${encodeURIComponent(workflowId)}`;

  const visible = useMemo(() => {
    const activeBatches = batches.filter((b) => {
      if (filters.status && filters.status !== "all" && b.status !== filters.status) return false;
      if (filters.agent_id && b.agent !== filters.agent_id) return false;

      if (filters.search) {
        const q = filters.search.toLowerCase();
        const searchField = filters.search_field || "all";
        let matches = false;
        if (searchField === "all") {
          if (b.name?.toLowerCase().includes(q)) matches = true;
          if (b.schedule_id?.toLowerCase().includes(q)) matches = true;
          if (b.agent?.toLowerCase().includes(q)) matches = true;
        } else if (searchField === "contact_id") {
          if (b.name?.toLowerCase().includes(q)) matches = true;
        } else if (searchField === "agent") {
          if (b.agent?.toLowerCase().includes(q)) matches = true;
        } else if (searchField === "schedule_id") {
          if (b.schedule_id?.toLowerCase().includes(q)) matches = true;
        }
        if (!matches) return false;
      }

      if (view === "table" && dateRange && !filters.search) {
        const dateStr = b.created_at || b.schedule_date;
        if (dateStr) {
          const d = new Date(dateStr);
          if (!Number.isNaN(d.getTime())) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (d < dateRange.start || d > endOfDay) return false;
          }
        }
      }
      return true;
    });

    if (filters.search && archiveResults.length > 0) {
      return [...activeBatches, ...archiveResults];
    }
    return activeBatches;
  }, [batches, filters, dateRange, view, archiveResults]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 p-6">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <button
            onClick={() => nav("/home/automation")}
            className="mb-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-3 hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Workflows
          </button>
          <h1 className="text-sm font-semibold text-ink">{workflow?.name || "Workflow batches"}</h1>
          <p className="text-sm text-ink-2">Batches launched from this workflow — same board as Batch Workspace</p>
        </div>
        <div className="flex items-center gap-2">
          {view === "board" && (
            <FilterPopover variant="batches" value={filters} onChange={setFilters} />
          )}
          <div className="flex rounded-lg border border-line p-1">
            {([{ id: "board", icon: SquareKanban }, { id: "table", icon: Table2 }] as const).map(({ id, icon: I }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                aria-label={`${id} view`}
                className={cn("rounded-md p-1.5", view === id ? "bg-brand-soft text-brand" : "text-ink-3 hover:bg-soft")}
              >
                <I className="h-4 w-4" />
              </button>
            ))}
          </div>
          <button
            onClick={() => setArchive({ status: "all" })}
            className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm font-semibold text-ink hover:bg-soft"
          >
            <Archive className="h-4 w-4" /> Archive
          </button>
          <button
            onClick={() => nav(scheduleHref)}
            className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
          >
            <Plus className="h-4 w-4" /> Schedule Workflow
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 flex flex-col">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-ink-3">Loading batches...</div>
        ) : view === "board" ? (
          <KanbanBoard
            batches={visible}
            columns={WORKFLOW_BOARD_COLUMNS}
            columnStatuses={WORKFLOW_COLUMN_STATUSES}
            onSelect={openRuns}
            onMenu={handleMenu}
            onShowAll={() => {
              setFilters((prev) => ({ ...prev, status: "all" }));
              setView("table");
            }}
            onQuickAdd={() => nav(scheduleHref)}
          />
        ) : (
          <div className="h-full overflow-auto">
            <BatchesTable
              batches={visible}
              variant="workflow"
              onRowClick={openRuns}
              filtersSlot={
                <>
                  <DateRangePicker value={dateRange} onChange={setDateRange} align="right" />
                  <FilterPopover variant="batches" value={filters} onChange={setFilters} />
                </>
              }
            />
          </div>
        )}
      </div>

      {archive && (
        <ArchiveDrawer
          isOpen
          onClose={() => setArchive(null)}
          initialStatus={archive.status}
          workflowId={workflowId}
          onSelect={(batch) => {
            setArchive(null);
            openRuns(batch);
          }}
        />
      )}
    </div>
  );
}
